// @ts-nocheck
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useAuth } from '@/auth/clerkShim';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import PageEditor4 from '@/components/PageEditor/PageEditor4';
import useLayoutStore from '@store/layoutStore';
import helpers from '@/utils/helpers';

interface Props {
  divisionId: string;
  divisionName: string;
  organizationId: string;
  leagueId?: string | null;
  leagueName: string;
}

/**
 * WYSIWYG editor for a division's `main`-page Layout document. Mirrors the
 * save/upsert behavior of HomeEditorAdmin but scoped to one division so we
 * don't have to send admins to /editor just to tweak the header HTML on a
 * single division page.
 *
 * Looks up an existing Layout with { tier: 'division', pageLocation: 'main',
 * division: divisionId } — updates it if found, otherwise creates a new one.
 */
export default function DivisionLayoutEditor({
  divisionId,
  divisionName,
  organizationId,
  leagueId,
  leagueName,
}: Props) {
  const { getToken } = useAuth();
  const [draftHtml, setDraftHtml] = useState('');

  const { layouts, fetchLayoutsByDivisionId, updateLayout, addLayout } = useLayoutStore((state: any) => ({
    layouts: state.layouts,
    fetchLayoutsByDivisionId: state.fetchLayoutsByDivisionId,
    updateLayout: state.updateLayout(getToken),
    addLayout: state.addLayout(getToken),
  }));

  useEffect(() => {
    if (divisionId) fetchLayoutsByDivisionId(divisionId);
  }, [divisionId, fetchLayoutsByDivisionId]);

  const currentLayout = _.find(layouts, (l: any) =>
    l.tier === 'division' &&
    l.pageLocation === 'main' &&
    helpers.getObjId(l.division) === divisionId,
  );

  const handleSave = async (html: string) => {
    if (currentLayout) {
      await updateLayout({ ...currentLayout, html });
    } else {
      await addLayout({
        name: divisionName,
        pageLocation: 'main',
        tier: 'division',
        html,
        active: true,
        order: 0,
        division: divisionId,
        organization: organizationId,
        ...(leagueId ? { league: leagueId } : {}),
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Division Page Content</CardTitle>
        <CardDescription>
          Custom HTML shown above the schedule on the public division page
          ({`/division/${divisionId}`}).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-md overflow-hidden">
          <PageEditor4
            currentLayout={currentLayout || { name: divisionName, html: '' }}
            onUpdate={setDraftHtml}
            onSave={handleSave}
            leagueName={leagueName}
          />
        </div>
      </CardContent>
    </Card>
  );
}
